import React, { useMemo, useState } from 'react';
import Modal from 'react-modal';
import { useMutation } from '@apollo/react-hooks';

import { StripePricingPlan, StripeCheckoutSessionResponse } from '../../../../models/Stripe';
import { BillingAccount } from '../../../../models/BillingAccount';
import { graphqlSchema } from '../../../../services/graphql.schema';
import { numberToCurrencyString } from '../../../../services/formatting';
import sharedToasterSubject from '../../../../services/shared.toasterSubject';
import GraphQlClient from '../../../../services/graphql';
import Button from '../../../Forms/Button';
import { ReactComponent as StarIcon } from './starIcon.svg';
import AccountPlansConfirm from './AccountPlans.confirm';

import './AccountPlans.scss';
import './AccountPlansModal.scss';

interface AccountPlansModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
    pricingPlans: StripePricingPlan[];
    isClusterAccount: boolean;
    billingAccount: BillingAccount | null;
}

const DEFAULT_VISIBLE_COUNT = 3;

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        maxHeight: '90vh',
        maxWidth: '480px',
        padding: '0px',
        border: 'none',
        borderRadius: '16px',
        boxShadow: '3px 4px 15px rgba(170, 170, 208, 0.5)',
        overflow: 'auto'
    }
};
Modal.setAppElement('#root');

function AccountPlansModal({
    isOpen,
    onRequestClose,
    pricingPlans,
    isClusterAccount,
    billingAccount
}: AccountPlansModalProps) {
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
    const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
    const [showAll, setShowAll] = useState(false);

    const [requestCheckoutSession, { loading: checkoutLoading }] = useMutation(graphqlSchema.STRIPE.PRICES.createCheckoutSession, {
        onCompleted: ({ createCheckoutSession }: { createCheckoutSession: StripeCheckoutSessionResponse }) => {
            console.log('Checkout Session Created [Modal]: ', createCheckoutSession);
            if (createCheckoutSession?.url) {
                window.location.href = createCheckoutSession.url;
            }
        },
        onError: (err) => {
            console.error('Error creating checkout session [Modal]:', err.message);
            sharedToasterSubject.next({ type: 'error', message: 'Error al iniciar el proceso de pago. Por favor intenta de nuevo o contacta al soporte.' });
        }
    });

    const [changePlan, { loading: changePlanLoading }] = useMutation(graphqlSchema.STRIPE.PRICES.changeSubscriptionPlan, {
        onCompleted: ({ changeSubscriptionPlan }: { changeSubscriptionPlan: boolean }) => {
            if (changeSubscriptionPlan) {
                sharedToasterSubject.next({ type: 'confirm', message: 'Plan cambiado exitosamente.' });
                GraphQlClient.recallCurrentUser();
                onRequestClose();
            } else {
                sharedToasterSubject.next({ type: 'error', message: 'No se pudo cambiar el plan. Por favor intenta de nuevo o contacta al soporte.' });
            }
        },
        onError: (err) => {
            console.error('Error changing subscription plan [Modal]:', err.message);
            sharedToasterSubject.next({ type: 'error', message: 'Error al cambiar de plan. Por favor intenta de nuevo o contacta al soporte.' });
        }
    });

    const activePriceId = billingAccount?.hasStripeSubscription
        ? billingAccount.stripeSubscriptionCfdiUsage.priceId
        : null;

    const isMaxedOut = billingAccount?.hasStripeSubscription
        ? billingAccount.stripeSubscriptionMaxedOut
        : false;

    const availablePlans = useMemo(() => {
        // Metered plans only available to users that have previously paid
        const hasPaid = !!(billingAccount?.lastPaymentId || billingAccount?.hasStripeSubscription);
        const _plans = hasPaid ? pricingPlans : pricingPlans.filter(p => !p.isMeteredBilling);
        const sorted = [..._plans].sort((a, b) => a.maxUsage - b.maxUsage);
        if (!activePriceId) return sorted;
        const activePlan = sorted.find(p => p.id === activePriceId);
        if (!activePlan) return sorted;
        return sorted.filter(p => p.maxUsage >= activePlan.maxUsage);
    }, [billingAccount, pricingPlans, activePriceId]);

    const recommendedPlanId = useMemo(() => {
        if (!isMaxedOut || !activePriceId) return null;
        const activeIndex = availablePlans.findIndex(p => p.id === activePriceId);
        if (activeIndex < 0 || activeIndex + 1 >= availablePlans.length) return null;
        return availablePlans[activeIndex + 1].id;
    }, [isMaxedOut, activePriceId, availablePlans]);

    const visiblePlans = useMemo(() => {
        if (showAll) return availablePlans;
        return availablePlans.slice(0, DEFAULT_VISIBLE_COUNT);
    }, [availablePlans, showAll]);

    const handleConfirm = () => {
        if (!selectedPlanId) return;
        if (checkoutLoading || changePlanLoading) return;

        if (billingAccount?.hasStripeSubscription) {
            setPendingPlanId(selectedPlanId);
            return;
        }
        requestCheckoutSession({
            variables: { pricingId: selectedPlanId }
        });
    };

    const handleConfirmPlanChange = () => {
        if (!pendingPlanId) return;
        changePlan({ variables: { pricingId: pendingPlanId } });
        setPendingPlanId(null);
    };

    if (isClusterAccount) return null;

    const confirmFromPlan = pendingPlanId ? pricingPlans.find(p => p.id === activePriceId) : null;
    const confirmToPlan = pendingPlanId ? pricingPlans.find(p => p.id === pendingPlanId) : null;
    const isConfirming = checkoutLoading || changePlanLoading;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            shouldCloseOnEsc={!isConfirming}
            shouldCloseOnOverlayClick={!isConfirming}
            style={customStyles}
            ariaHideApp={false}
            contentLabel="Elije tu plan de facturación"
        >
            <div id="AccountPlansModal">
                <div className='planSelectSection'>
                    <h4 className='title'>Elije tu Plan de Facturación</h4>
                    <p className='subtitle'>
                        Podrás cambiar o cancelar tu plan en cualquier momento.
                    </p>

                    <div className='plansContainer'>
                        <div className='plansList'>
                            {visiblePlans.map((plan) => {
                                const isActive = plan.id === activePriceId;
                                const isSelected = plan.id === selectedPlanId;
                                const isRecommended = plan.id === recommendedPlanId;
                                const isAboveThousand = plan.maxUsage >= 1000;
                                const className = [
                                    'planEntry',
                                    isActive ? 'active' : '',
                                    isSelected ? 'selected' : '',
                                    isRecommended ? 'recommended' : '',
                                    isAboveThousand && !recommendedPlanId ? 'aboveThousand' : '',
                                ].filter(Boolean).join(' ');

                                return (
                                    <div
                                        key={plan.id}
                                        className={className}
                                        onClick={() => !isActive && setSelectedPlanId(plan.id)}
                                    >
                                        <div className={`planInfo`}>
                                            <p className='planName'>{plan.name}</p>
                                            <p className='planPrice'>
                                                ${numberToCurrencyString(plan.unitAmountCents / 100)} MXN
                                            </p>
                                            {
                                                plan.isMeteredBilling ?
                                                    <p className='planCfdiCost'>
                                                        Hasta ${numberToCurrencyString(plan.cfdiMinAmountCents / 100)} MXN por factura <br />
                                                        Factura adicional a $0.60 MXN
                                                    </p>
                                                    :
                                                    <p className='planCfdiCost'>
                                                        Hasta ${numberToCurrencyString(plan.cfdiMinAmountCents / 100)} MXN por factura
                                                    </p>
                                            }
                                        </div>
                                        <div className='planUsage'>
                                            {
                                                isActive && <span className={`chip ${isActive ? 'active' : 'hidden'}`}>Activo</span>
                                            }
                                            {
                                                isRecommended && <span className={`chip ${isRecommended ? 'recommended' : 'hidden'}`}>
                                                    Sugerido
                                                    <StarIcon />
                                                </span>
                                            }
                                            {
                                                !isActive && !isRecommended && <span className={`chip 'hidden'`}></span>
                                            }
                                            <p className='maxUsage'>{plan.maxUsage.toLocaleString()} facturas</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {availablePlans.length > DEFAULT_VISIBLE_COUNT && (
                            <Button tertiary size='small' handleClick={() => setShowAll(!showAll)}>
                                <span>{showAll ? 'Ver menos planes' : 'Ver todos los planes disponibles'}</span>
                            </Button>
                        )}
                    </div>
                </div>
                <div className='actionRow'>
                    <Button
                        primary
                        size='small'
                        disabled={!selectedPlanId || isConfirming}
                        handleClick={handleConfirm}
                    >
                        <span>{isConfirming ? 'Procesando...' : 'Confirmar'}</span>
                    </Button>
                    <Button
                        tertiary
                        size='small'
                        disabled={isConfirming}
                        handleClick={onRequestClose}
                    >
                        <span>Cancelar</span>
                    </Button>
                </div>
                {!!pendingPlanId && billingAccount?.hasStripeSubscription && confirmFromPlan && confirmToPlan && (
                    <AccountPlansConfirm
                        isOpen={true}
                        fromPlan={confirmFromPlan}
                        toPlan={confirmToPlan}
                        billingAccount={billingAccount}
                        onCancel={() => setPendingPlanId(null)}
                        onConfirm={handleConfirmPlanChange}
                    />
                )}
            </div>
        </Modal>
    );
}

export default AccountPlansModal;
